import React, { createContext } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { get, put, post } from "utils/api";
import { getApiErrorMessage } from "utils/misc";
import useFetch from "hooks/useFetch";
import { errorMessage } from "actions/message";
import useTasks from "features/tasks/use-tasks";

export const OrderProfileContext = createContext();

const OrderProfileNewProvider = ({ orderId, children }) => {
  const dispatch = useDispatch();

  const [id, setId] = React.useState(orderId);

  const fetchOrderProfileApi = useFetch({
    apiFn: orderId => get(`order-profile/${orderId}`),
    onError: () => dispatch(errorMessage("Error Fetching Order")),
    defaultValue: {}
  });

  const updateOrderProfileApi = useFetch({
    apiFn: values => put(`orders/${orderId}`, values),
    defaultValue: {},
    onSuccess: () => fetchOrderProfileApi.fetchData(orderId),
    onError: error =>
      dispatch(errorMessage(getApiErrorMessage(error, "Error updating order")))
  });

  const setOrderProfileAccessApi = useFetch({
    apiFn: orderId => post(`orders/${orderId}/access`, {})
  });

  const fetchOrderLineItemsApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/line-items`),
    defaultValue: [],
    clearDataOnFetch: false
  });

  const createOrderNoteApi = useFetch({
    apiFn: values => post(`orders/${orderId}/notes`, values),
    defaultValue: {},
    onSuccess: () => fetchOrderNotesApi.fetchData(orderId),
    onError: error =>
      dispatch(errorMessage(getApiErrorMessage(error, "Failed to create note")))
  });

  const fetchOrderNotesApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/notes`),
    defaultValue: [],
    transformResponse: response => response?.notes ?? []
  });

  const fetchOrderEquipmentApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/equipment_requested`),
    defaultValue: {
      compatibleItems: {
        patientCompatibleItems: [],
        orderCompatibleItems: [],
        patientExcludedHCPCs: []
      },
      equipment_requested: []
    }
  });

  const addOrderLineItemByIdApi = useFetch({
    apiFn: ({ product_id, addQuantity }) =>
      post(`orders/${orderId}/add_line_item_by_id`, {
        product_id,
        addQuantity
      }),
    onSuccess: () => {
      fetchOrderEquipmentApi.fetchData(orderId);
      fetchOrderLineItemsApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error adding line item"))
      )
  });

  const updateOrderLineItemByIdApi = useFetch({
    apiFn: values => post(`orders/${orderId}/update_line_item_by_id`, values),
    onSuccess: () => fetchOrderLineItemsApi.fetchData(orderId),
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error updating line item"))
      )
  });

  const addOrderLineItemApi = useFetch({
    apiFn: ({ id, addQuantity }) =>
      post(`orders/${orderId}/add_line_item`, {
        id,
        addQuantity
      }),
    onSuccess: () => {
      fetchOrderEquipmentApi.fetchData(orderId);
      fetchOrderLineItemsApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error adding line item"))
      )
  });

  const updateTrackingApi = useFetch({
    apiFn: payload => post(`orders/${orderId}/add_shipping_details`, payload),
    onSuccess: () => {
      fetchOrderLineItemsApi.fetchData(orderId);
      fetchOrderProfileApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error updating tracking"))
      )
  });

  // JANKY hack to prevent re-fetching when orderId reference changes
  React.useEffect(() => {
    if (orderId != id) {
      setId(orderId);
    }
  }, [orderId]);

  React.useEffect(() => {
    fetchOrderProfileApi.fetchData(orderId);
    fetchOrderLineItemsApi.fetchData(orderId);
    fetchOrderNotesApi.fetchData(orderId);
    setOrderProfileAccessApi.fetchData(orderId);
    fetchOrderEquipmentApi.fetchData(orderId);
  }, [id]);

  // JANKY hack to prevent re-fetching when orderId reference changes
  const [patientId, setPatientId] = React.useState(
    fetchOrderProfileApi.response?.patient_id
  );
  React.useEffect(() => {
    if (patientId != fetchOrderProfileApi.response?.patient_id) {
      setPatientId(fetchOrderProfileApi.response?.patient_id);
    }
  }, [fetchOrderProfileApi.response?.patient_id]);

  const {
    tasks,
    createTask,
    createTaskNote,
    getPatientTasks,
    updateTask,
    isFetchingTasks
  } = useTasks(patientId);

  const patientFormAttributesAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/form-attributes`),
    defaultValue: {
      company_insurances: [],
      accessible_users: { dmes: [], s3_users: [] }
    }
  });

  React.useEffect(() => {
    if (patientId) {
      patientFormAttributesAPI.fetchData(patientId);
      getPatientTasks(patientId);
    }
  }, [patientId]);

  const order = fetchOrderProfileApi?.response;
  const isFetchingOrder = fetchOrderProfileApi?.isFetching;
  return (
    <OrderProfileContext.Provider
      value={{
        order,
        fetchOrderProfile: fetchOrderProfileApi.fetchData,
        isFetchingOrder,
        tasks,
        createTask,
        createTaskNote,
        getPatientTasks,
        updateTask,
        isFetchingTasks,
        updateOrder: updateOrderProfileApi.fetchData,
        isUpdatingOrder: updateOrderProfileApi.isFetching,
        fetchOrderLineItemsApi,
        createOrderNoteApi,
        fetchOrderNotesApi,
        updateTrackingApi,
        addOrderLineItemByIdApi,
        addOrderLineItemApi,
        updateOrderLineItemByIdApi,
        fetchOrderEquipmentApi,
        patientFormAttributesAPI
      }}
    >
      {children}
    </OrderProfileContext.Provider>
  );
};

OrderProfileNewProvider.propTypes = {
  orderId: PropTypes.string,
  children: PropTypes.node
};

export default OrderProfileNewProvider;
